import * as React from 'react'
import { Text } from 'DVWebNodeUI'
import * as st from './playback.module.scss'
import * as WpsRuntime from 'DVWebNodeWpsRuntime'
import { RecordRefTag } from '../recordReftag/recordReftag'
import { getPlaybackClickstreamRef } from '../../utils/getPlaybackClickstreamRef'
import { getPlaybackProgress } from '../../utils/getPlaybackProgress'
import {
  DeeplinkProps,
  PlayBox,
  SquarePlayButton
} from '../playButton/PlayButton'
import { initializePlayer } from '../../integrations/player/index'
import { PlayerConfigData } from '../../integrations/player/config'
import { tryCatch } from '../../utils/tryCatch'

import {
  UWPNotifier,
  OutputAction,
  InputAction,
  DownloadStatusPayload,
  DownloadStatus,
  SubscribeReturn
} from '@shared'

type PlaybackActionState = {
  downloadCompleted: boolean
}

type Handler = undefined | ((e: React.MouseEvent) => void)

const buildSquarePlayButton = (
  deeplinkingData: DeeplinkProps,
  playbackData: WpsRuntime.atv.wps.PlaybackActionDataProps,
  handler: Handler,
  isStartOver?: boolean
) => (
  <SquarePlayButton
    deepLinkingData={deeplinkingData}
    href={playbackData.playbackURL}
    onClick={handler}
    progress={getPlaybackProgress(playbackData.progress)}
    forceDoubleHeight={true}
    isStartOver={isStartOver}
  >
    <Text tag="span" string={playbackData.label} />
  </SquarePlayButton>
)

const buildCirclePlayButton = (
  deeplinkingData: DeeplinkProps,
  playbackData: WpsRuntime.atv.wps.PlaybackActionDataProps,
  isHover: boolean,
  handler: Handler,
  isStartOver?: boolean
) => (
  <PlayBox
    deepLinkingData={deeplinkingData}
    href={playbackData.playbackURL}
    onClick={handler}
    progress={getPlaybackProgress(playbackData.progress)}
    isLarge={!isHover}
    isStartOver={isStartOver}
    isHover={isHover}
  >
    <Text tag="span" string={playbackData.label} />
  </PlayBox>
)

export type OnlineOfflineRenderProps = {
  downloadCompleted: boolean
}
type OnlineOfflinePlaybackProps = {
  playbackTitleId: string
  render: (props: OnlineOfflineRenderProps) => JSX.Element
}
export class OnlineOfflinePlayback extends React.Component<
  OnlineOfflinePlaybackProps,
  PlaybackActionState
> {
  unsubscribe: SubscribeReturn

  state = {
    downloadCompleted: false
  }

  downloadStatusChanged = (payload: DownloadStatusPayload) => {
    const { state = DownloadStatus.IDLE, titleId } = payload
    const { playbackTitleId } = this.props

    if (titleId && playbackTitleId && titleId === playbackTitleId) {
      this.setState({
        downloadCompleted:
          DownloadStatus[state as keyof typeof DownloadStatus] ===
          DownloadStatus.COMPLETED
      })
    }
  }

  componentDidMount() {
    this.unsubscribe = UWPNotifier.getInstance().subscribe(
      InputAction.DownloadStatus,
      this.downloadStatusChanged
    )
  }

  componentWillUnmount() {
    this.unsubscribe && this.unsubscribe()
  }

  render() {
    return this.props.render({
      downloadCompleted: this.state.downloadCompleted
    })
  }
}

export interface IPlaybackProps {
  playbackData?: WpsRuntime.atv.wps.PlaybackActionDataProps
  playerConfigData: PlayerConfigData
  sessionId: string
  pageTitleId: string
  usePlayer: boolean
  features?: string | string[]
  isRetail?: boolean
  isHover?: boolean
  isStartOver?: boolean
  downloadCompleted?: boolean
}

export const Playback = tryCatch(
  ({
    playbackData,
    playerConfigData,
    sessionId,
    pageTitleId,
    isRetail,
    isStartOver,
    isHover = false,
    usePlayer,
    downloadCompleted
  }: IPlaybackProps) => {
    if (!playbackData) {
      return null
    }

    const playbackTitleId = playbackData.playbackID

    const deeplinkingData = {
      fallbackUrl: playbackData.fallbackURL,
      titleId: playbackTitleId,
      resumeTime: playbackData.resumeTime,
      signInUrl: playbackData.signInURL,
      sessionId,
      pageTitleId,
      ref: getPlaybackClickstreamRef(
        playbackData.contentType,
        playbackData.entitlementType
      ),
      videoType: playbackData.videoMaterialType
    }

    const triggerOnlinePlayback: Handler = e => {
      const contentConfig = {
        asin: playbackTitleId,
        trailer: false,
        position: playbackData.resumeTime,
        ref: getPlaybackClickstreamRef(
          playbackData.contentType,
          playbackData.entitlementType
        ),
        videoType: playbackData.videoMaterialType,
        useFullView: true
      }

      initializePlayer(playerConfigData).play(contentConfig)
      e.preventDefault()
    }

    const triggerOfflinePlayback: Handler = e => {
      UWPNotifier.getInstance().publish(OutputAction.PlayDownload, {
        titleId: playbackTitleId
      })
      e.preventDefault()
    }

    const triggerPlayback = downloadCompleted ? triggerOfflinePlayback : triggerOnlinePlayback
    const handler = usePlayer ? triggerPlayback : undefined

    const playButton =
      isRetail && !isHover
        ? buildSquarePlayButton(
            deeplinkingData,
            playbackData,
            handler,
            isStartOver
          )
        : buildCirclePlayButton(
            deeplinkingData,
            playbackData,
            isHover,
            handler,
            isStartOver
          )

    return (
      <RecordRefTag
        refTag="atv_dp_pb_main"
        className="avu-full-width-phablet dv-dp-node-playback"
      >
        <div className={st.playback}>{playButton}</div>
      </RecordRefTag>
    )
  }
)

export const ActionBoxPlayback = (props: IPlaybackProps) => {
  const renderPlayback = ({ downloadCompleted }: OnlineOfflineRenderProps) => {
    return <Playback {...props} downloadCompleted={downloadCompleted} />
  }

  const playbackTitleId = props.playbackData && props.playbackData.playbackID

  return (
    <OnlineOfflinePlayback
      playbackTitleId={playbackTitleId}
      render={renderPlayback}
    />
  )
}
